import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, PauseCircle, PlayCircle } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  listStoreTargetDeals,
  createStoreTargetDeal,
  updateStoreTargetDeal,
  deleteStoreTargetDeal,
} from "@/lib/api/admin";
import type { TargetDeal } from "@/lib/api/types";
import { formatCents } from "@/lib/utils/number";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { Spinner } from "@/components/Spinner";
import { TargetDealFormDialog } from "../dialogs/TargetDealFormDialog";

type TargetDealsTabProps = {
  storeId: string;
  canManageAppointments: boolean;
};

export function TargetDealsTab({ storeId, canManageAppointments }: TargetDealsTabProps) {
  const user = useAdminAuth();
  const qc = useQueryClient();

  const [formOpen, setFormOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [editingItem, setEditingItem] = useState<TargetDeal | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<TargetDeal | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const { data: targetDeals, isLoading } = useQuery({
    queryKey: ["target-deals", storeId],
    queryFn: () => listStoreTargetDeals(user!, storeId),
    enabled: !!user,
    refetchOnMount: "always",
  });

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ["target-deals", storeId] });
  }

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof createStoreTargetDeal>[2]) =>
      createStoreTargetDeal(user!, storeId, data),
    onSuccess: () => {
      invalidate();
      setFormOpen(false);
      toast.success("Target discount created.");
    },
    onError: (err) => setApiError((err as Error).message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateStoreTargetDeal>[3] }) =>
      updateStoreTargetDeal(user!, storeId, id, data),
    onSuccess: () => {
      invalidate();
      setFormOpen(false);
      setEditingItem(undefined);
      toast.success("Target discount updated.");
    },
    onError: (err) => setApiError((err as Error).message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteStoreTargetDeal(user!, storeId, id),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
      toast.success("Target deal deleted.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (td: TargetDeal) =>
      updateStoreTargetDeal(user!, storeId, td.id, {
        name: td.name,
        discount_type: td.discount_type,
        discount_percent: td.discount_percent,
        fixed_price_cents: td.fixed_price_cents,
        max_claims_per_customer_per_day: td.max_claims_per_customer_per_day ?? null,
        schedule: td.schedule,
        status: td.status === "active" ? "inactive" : "active",
        category_ids: td.category_ids ?? [],
        item_ids: td.item_ids ?? [],
        excluded_item_ids: td.excluded_item_ids ?? [],
        appointment_service_ids: td.appointment_service_ids ?? [],
      }),
    onSuccess: (_, td) => {
      invalidate();
      toast.success(td.status === "active" ? "Target deal paused." : "Target deal activated.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const formPending = createMutation.isPending || updateMutation.isPending;

  function handleOpenAdd() {
    setEditingItem(undefined);
    setApiError(null);
    setFormKey((k) => k + 1);
    setFormOpen(true);
  }

  function handleOpenEdit(td: TargetDeal) {
    setEditingItem(td);
    setApiError(null);
    setFormKey((k) => k + 1);
    setFormOpen(true);
  }

  function handleSave(data: {
    name: string;
    discount_type: "percent" | "fixed_price";
    discount_percent: number | null;
    fixed_price_cents: number | null;
    max_claims_per_customer_per_day: number | null;
    schedule: Record<string, { open: string; close: string }[]>;
    status: "active" | "inactive";
    category_ids: string[];
    item_ids: string[];
    excluded_item_ids: string[];
    appointment_service_ids: string[];
  }) {
    setApiError(null);
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data });
    } else {
      createMutation.mutate(data);
    }
  }

  const items = targetDeals ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Button size="sm" onClick={handleOpenAdd}>
          <Plus className="size-4 mr-1" /> Add Target Deal
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner className="size-5 border-primary" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title="No target deals"
          description="Create a target deal to discount a set of items during a recurring window."
        />
      ) : (
        <div className="space-y-2">
          {items.map((td) => (
            <Card
              key={td.id}
              onClick={() => handleOpenEdit(td)}
              className="cursor-pointer hover:border-primary/50"
            >
              <CardContent className="flex items-center gap-3 p-3">
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm truncate">{td.name}</p>
                    <StatusBadge status={td.status} />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {td.discount_type === "fixed_price"
                      ? `${formatCents(td.fixed_price_cents ?? 0)} each`
                      : `${td.discount_percent}% off`}{" "}
                    · {td.item_count} item
                    {td.item_count === 1 ? "" : "s"}
                    {td.max_claims_per_customer_per_day != null &&
                      ` · max ${td.max_claims_per_customer_per_day}/day per customer`}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    title={td.status === "active" ? "Pause" : "Activate"}
                    disabled={toggleStatusMutation.isPending}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleStatusMutation.mutate(td);
                    }}
                  >
                    {td.status === "active" ? (
                      <PauseCircle className="size-3.5" />
                    ) : (
                      <PlayCircle className="size-3.5" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 text-muted-foreground enabled:hover:text-destructive enabled:hover:bg-destructive/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(td);
                    }}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete target deal"
        description={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isPending={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
      />

      <TargetDealFormDialog
        key={formKey}
        open={formOpen}
        onOpenChange={setFormOpen}
        storeId={storeId}
        item={editingItem}
        onSave={handleSave}
        isPending={formPending}
        apiError={apiError}
        canManageAppointments={canManageAppointments}
      />
    </div>
  );
}
